import { Trans, useLingui } from "@lingui/react/macro";
import React from "react";
import InputWithError from "components/Form/InputWithError";
import { UseFormRegister } from "react-hook-form";
import { LeadFormData } from "./types";
import {
  InputCheckbox,
  InputText,
  Select,
  TextArea
} from "@silversea-ssc/ui-components-react";
import { Country } from "./utils";
import {
  textAreaForbiddenChars,
  textForbiddenChars
} from "components/Form/utils";

export const InputFormTitle: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="Title"
      id="subscribe-modal-title"
      label={`${t`Form Title`} *`}
    >
      <Select
        {...register("Title", {
          required: i18n._(t`form-required-title`)
        })}
        dataContentPiece="form_title_select"
        defaultValue=""
        autoComplete="honorific-prefix"
      >
        <option value=""></option>
        <option value="Mr">{t`Form Title Mr.`}</option>
        <option value="Mrs">{t`Form Title Mrs.`}</option>
        <option value="Miss">{t`Form Title Miss.`}</option>
        <option value="Ms">{t`Form Title Ms.`}</option>
        <option value="Dr">{t`Form Title Dr.`}</option>
        <option value="Dir">{t`Form Title Dir.`}</option>
        <option value="Lady">{t`Form Title Lady.`}</option>
        <option value="Prof">{t`Form Title Prof.`}</option>
        <option value="Lord">{t`Form Title Lord.`}</option>
      </Select>
    </InputWithError>
  );
};

export const InputFormNameFirst: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="NameFirst"
      id="subscribe-modal-name-first"
      label={`${t`Form First name`} *`}
      autocomplete="given-name"
    >
      <InputText
        {...register("NameFirst", {
          required: i18n._(t`form-required-firstname`),
          validate: (value) =>
            textForbiddenChars.test(value ?? "")
              ? i18n._(t`form-invalid-characters`)
              : true
        })}
        type="text"
        autoComplete="given-name"
      />
    </InputWithError>
  );
};

export const InputFormNameLast: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="NameLast"
      id="subscribe-modal-name-last"
      label={`${t`Form Last name`} *`}
    >
      <InputText
        {...register("NameLast", {
          required: i18n._(t`form-required-lastname`),
          validate: (value) =>
            textForbiddenChars.test(value ?? "")
              ? i18n._(t`form-invalid-characters`)
              : true
        })}
        type="text"
        autoComplete="family-name"
      />
    </InputWithError>
  );
};

export const InputFormCountry: React.FC<{
  register: UseFormRegister<LeadFormData>;
  countries: Country[];
}> = ({ register, countries }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="Country"
      id="subscribe-modal-address-country"
      label={`${t`Country`} *`}
    >
      <Select
        {...register("Country", {
          required: i18n._(t`form-required-country`)
        })}
        dataContentPiece="form_country_select"
        autoComplete="country-name"
      >
        <option value=""></option>
        {countries?.map((x) => (
          <option key={x.name} value={x.iso3Code}>
            {x.name}
          </option>
        ))}
      </Select>
    </InputWithError>
  );
};

export const InputFormAddress1: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="Address1"
      id="subscribe-modal-address-1"
      label={`${t`Postal address`} *`}
    >
      <InputText
        {...register("Address1", {
          required: i18n._(t`form-required-postal-address`),
          validate: (value) =>
            textForbiddenChars.test(value ?? "")
              ? i18n._(t`form-invalid-characters`)
              : true
        })}
        type="text"
        autoComplete="address-line1"
      />
    </InputWithError>
  );
};

export const InputFormZip: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="Zip"
      id="subscribe-modal-address-zip"
      label={`${t`Postal code`} *`}
    >
      <InputText
        {...register("Zip", {
          required: i18n._(t`form-required-postal-code`),
          validate: (value) =>
            textForbiddenChars.test(value ?? "")
              ? i18n._(t`form-invalid-characters`)
              : true
        })}
        type="text"
        autoComplete="postal-code"
      />
    </InputWithError>
  );
};
export const InputFormCity: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      name="City"
      id="subscribe-modal-city"
      label={`${t`City`} *`}
    >
      <InputText
        {...register("City", {
          required: i18n._(t`form-required-city`),
          validate: (value) =>
            textForbiddenChars.test(value ?? "")
              ? i18n._(t`form-invalid-characters`)
              : true
        })}
        type="text"
        autoComplete="address-level2"
      />
    </InputWithError>
  );
};

export const InputFormState: React.FC<{
  register: UseFormRegister<LeadFormData>;
  states: string[];
}> = ({ register, states }) => {
  const { i18n, t } = useLingui();
  return (
    <React.Fragment>
      {states.length > 0 && (
        <InputWithError
          name="State"
          id="subscribe-modal-address-state"
          label={`${t`State`} *`}
        >
          <Select
            {...register("State", {
              required: i18n._(t`form-required-state`)
            })}
            autoComplete="address-level1"
            dataContentPiece="form_state_select"
          >
            <option value=""></option>
            {states.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
        </InputWithError>
      )}
      {states.length === 0 && (
        <InputWithError
          name="State"
          id="subscribe-modal-address-state"
          label={`${t`State`}`}
        >
          <InputText
            {...register("State", {
              required: false,
              validate: (value) =>
                textForbiddenChars.test(value ?? "")
                  ? i18n._(t`form-invalid-characters`)
                  : true
            })}
            type="text"
            autoComplete="address-level1"
          />
        </InputWithError>
      )}
    </React.Fragment>
  );
};

export const InputFormEmail: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      id="subscribe-modal-email"
      name="Email"
      label={`${t`Form Email`} *`}
    >
      <InputText
        {...register("Email", {
          required: i18n._(t`form-required-email-address`),
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
            message: i18n._(t`form-invalid-email-address`)
          }
        })}
        type="email"
        autoComplete="email"
      />
    </InputWithError>
  );
};

export const InputFormDescription: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  const { i18n, t } = useLingui();
  return (
    <InputWithError
      id="subscribe-modal-details"
      name="Comments"
      label={`${i18n._(t`Details About Your Request`)}`}
    >
      <TextArea
        {...register("Comments", {
          validate: (value) =>
            textAreaForbiddenChars.test(value ?? "")
              ? i18n._(t`form-invalid-characters`)
              : true
        })}
      />
    </InputWithError>
  );
};

export const InputFormIsNotAgent: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  return (
    <InputCheckbox
      {...register("IsNotAgent")}
      id="subscribe-modal-form-im-not-travel-agent"
      name="IsNotAgent"
    >
      <Trans id="Form ImNotTravelAgent">I am not a travel agent</Trans>
    </InputCheckbox>
  );
};

export const InputFormWorkingWithAgent: React.FC<{
  register: UseFormRegister<LeadFormData>;
}> = ({ register }) => {
  return (
    <InputCheckbox
      {...register("WorkingWithAgent")}
      id="subscribe-modal-form-working-with-agent"
      name="WorkingWithAgent"
      reverseValue={0}
    >
      <Trans id="Form WorkWithAgent">
        I am currently booked on a Silversea cruise
      </Trans>
    </InputCheckbox>
  );
};

export const HiddenTrackingFields: React.FC<{
  register: UseFormRegister<LeadFormData>;
  brochureCode: boolean;
  enableSubscribeEmail: boolean;
  cruiseSuiteInfo: boolean;
}> = ({ register, brochureCode, enableSubscribeEmail, cruiseSuiteInfo }) => {
  return (
    <React.Fragment>
      <input
        type="hidden"
        {...register("RequestSource")}
        name="RequestSource"
      />
      <input type="hidden" {...register("RequestType")} name="RequestType" />
      <input type="hidden" {...register("SiteCountry")} name="SiteCountry" />
      <input type="hidden" {...register("SiteLanguage")} name="SiteLanguage" />
      <input type="hidden" {...register("SiteCurrency")} name="SiteCurrency" />
      <input type="hidden" {...register("AUID")} name="AUID" />
      <input type="hidden" {...register("MCId")} name="MCId" />
      {brochureCode && (
        <input
          type="hidden"
          {...register("BrochuresRequested")}
          name="BrochuresRequested"
        />
      )}
      {enableSubscribeEmail && (
        <input
          type="hidden"
          {...register("SubscribeEmail")}
          name="SubscribeEmail"
        />
      )}
      {cruiseSuiteInfo && (
        <React.Fragment>
          <input type="hidden" {...register("VoyageCod")} name="VoyageCod" />
          <input type="hidden" {...register("Voyage")} name="Voyage" />
          <input type="hidden" {...register("Ship")} name="Ship" />
          <input type="hidden" {...register("SailDate")} name="SailDate" />
          <input type="hidden" {...register("Price")} name="Price" />
          <input
            type="hidden"
            {...register("SuiteVariation")}
            name="SuiteVariation"
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
